// @file Surface post connection store
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { ACCESS_WITHDRAWN } from '@@/bits/snackbar_helper'
import { vDel } from '@@/bits/vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Id, PostConnection } from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'

type PostConnectionId = Id | string

const useSurfacePostConnectionStore = defineStore('surfacePostConnection', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const connectionsFetched = ref<boolean>(false)
  const connectionEntities = ref<Record<PostConnectionId, PostConnection>>({})
  const pendingConnectionIds = ref<Set<string>>(new Set())
  const postToConnectFromId = ref<Id | null>(null)
  const postToDisconnectFromId = ref<Id | null>(null)
  const snackBarUid = ref<string | null>(null)
  // Session tracking state
  const connectionsCreatedInSession = ref<Set<PostConnectionId>>(new Set())
  const disconnectionsCreatedInSession = ref<PostConnection[]>([])
  const MAX_TRACKED_SESSIONS = 10
  const sessionId = ref<number>(0)
  const disconnectionSessionId = ref<number>(0)
  const sessionsFinishedByUser = ref<Record<number, boolean>>({})
  const connectionFinishRequested = ref<boolean>(false)
  const disconnectionSessionsFinishedByUser = ref<Record<number, boolean>>({})

  // Check if multiple connections mode is enabled
  const useMultipleConnections = isAppUsing('surfaceConnectionsChange')

  // Track recently updated connection labels to prevent echo-back from realtime updates
  const recentlyUpdatedConnectionLabels = ref<Map<PostConnectionId, Set<string>>>(new Map())

  /* ---------------------- */
  /* GETTERS                */
  /* ---------------------- */

  const connections = computed<PostConnection[]>(() => {
    return connectionIds.value.map((id) => connectionEntities.value[id])
  })
  const currentConnections = computed<PostConnection[]>(() => {
    const currentPostIds: Id[] = surfacePostsStore.currentSortedPosts.flatMap((p) => (p.id != null ? [p.id] : []))
    return connections.value.filter(
      (c: PostConnection) => currentPostIds.includes(c.from_wish_id) && currentPostIds.includes(c.to_wish_id),
    )
  })
  const connectionIds = computed<PostConnectionId[]>(() => Object.keys(connectionEntities.value))

  const xConnectionCancellation = computed<boolean>((): boolean => postToConnectFromId.value != null)
  const xDisconnectionCancellation = computed<boolean>((): boolean => postToDisconnectFromId.value != null)

  /* ---------------------- */
  /* HELPER FUNCTIONS       */
  /* ---------------------- */

  const saveFetchedConnections = (connections: PostConnection[]): void => {
    if (connections != null) {
      const newConnectionEntities = {}
      connections.forEach((r) => {
        newConnectionEntities[r.id] = r
      })
      connectionEntities.value = newConnectionEntities // Trigger state change once
      pendingConnectionIds.value.clear()
    }
    connectionsFetched.value = true
  }

  const addPendingConnection = (pendingConnection: Partial<PostConnection>): string | undefined => {
    const tempId = 'new_' + String(new Date().valueOf())
    const newPendingConnection = {
      ...pendingConnection,
      id: tempId,
    } as unknown as PostConnection
    connectionEntities.value = {
      ...connectionEntities.value,
      // TODO: fix PostConnection type - pending connections use string IDs temporarily
      [tempId]: newPendingConnection as PostConnection & { id: string },
    }
    pendingConnectionIds.value.add(tempId)
    return useMultipleConnections ? tempId : undefined
  }

  const addConnection = (connection: PostConnection, isFromRealtime = false): void => {
    // Only skip if this is a realtime update and the label matches one of our optimistic updates
    // This allows other collaborators' edits to come through even during our update window
    if (isFromRealtime && recentlyUpdatedConnectionLabels.value.has(connection.id)) {
      const pendingLabels = recentlyUpdatedConnectionLabels.value.get(connection.id)
      if (pendingLabels != null && pendingLabels.has(connection.label)) {
        // This is our own update being echoed back, skip it
        return
      }
      // Label differs, this is another collaborator's edit - allow it through
    }

    const pendingConnectionIdRemoved = [...pendingConnectionIds.value].some((tempId) => {
      if (
        connectionEntities.value[tempId].from_wish_id === connection.from_wish_id &&
        connectionEntities.value[tempId].to_wish_id === connection.to_wish_id &&
        connectionEntities.value[tempId].label === connection.label
      ) {
        const newConnectionEntities = { ...connectionEntities.value }
        // delete pending connection
        vDel(newConnectionEntities, tempId)
        // put new connection in place of pending connection
        connectionEntities.value = {
          ...newConnectionEntities,
          [connection.id]: connection,
        }
        pendingConnectionIds.value.delete(tempId)
        return true
      }
      return false
    })
    if (!pendingConnectionIdRemoved) {
      connectionEntities.value = {
        ...connectionEntities.value,
        [connection.id]: connection,
      }
    }
  }

  const removeConnection = (id: PostConnectionId): void => {
    const newConnectionEntities = { ...connectionEntities.value }
    vDel(newConnectionEntities, id)
    connectionEntities.value = newConnectionEntities
  }

  const removeConnections = (ids: PostConnectionId[]): void => {
    const idsToRemove = new Set(ids)
    const newConnectionEntities = Object.fromEntries(
      Object.entries(connectionEntities.value).filter(([id]) => !idsToRemove.has(id)),
    )
    connectionEntities.value = newConnectionEntities
  }

  const getConnectionsFromPost = (postId: Id): PostConnection[] => {
    return connections.value.filter((c: PostConnection) => c.from_wish_id === postId)
  }

  const getConnectionsToPost = (postId: Id): PostConnection[] => {
    return connections.value.filter((c: PostConnection) => c.to_wish_id === postId)
  }

  /**
   * Remove a specific label from the tracking map for a connection.
   * If the Set becomes empty after removal, also remove the connection entry.
   */
  const removePendingLabel = (connectionId: PostConnectionId, label: string): void => {
    const pendingLabels = recentlyUpdatedConnectionLabels.value.get(connectionId)
    if (pendingLabels != null) {
      pendingLabels.delete(label)
      // Remove the Set if it's empty
      if (pendingLabels.size === 0) {
        recentlyUpdatedConnectionLabels.value.delete(connectionId)
      }
    }
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const fetchConnections = async (): Promise<void> => {
    try {
      const { PostConnectionApi } = await import('@@/surface/api/post_connection')
      const response = await PostConnectionApi.readAll({ wall: { id: surfaceStore.wallId } })
      fetchConnectionsSuccess({ fetchedData: response })
    } catch (e) {
      // Don't capture exception when it's 401, show a snackbar instead
      if (e.status === 401) {
        globalSnackbarStore.setSnackbar(ACCESS_WITHDRAWN)
      } else {
        captureNonNetworkFetchError(e, { source: 'fetchConnections' })
      }
    }
  }

  const fetchConnectionsSuccess = ({ fetchedData }: { fetchedData: JsonAPIResponse<PostConnection> }): void => {
    const connections = (fetchedData.data as Array<JsonAPIResource<PostConnection>>).map(
      (connection) => connection.attributes,
    )
    saveFetchedConnections(connections)
  }

  const cancelConnection = (): void => {
    if (useMultipleConnections) {
      // Remove all connections created during this session
      if (connectionsCreatedInSession.value.size > 0) {
        // Get all connections that exist (handles both pending string IDs and completed numeric IDs)
        const connectionsToDelete = [...connectionsCreatedInSession.value]
          .map((id) => connectionEntities.value[id])
          .filter((conn) => conn != null)

        // Remove each connection
        connectionsToDelete.forEach((connection) => {
          // Only delete from server if it's not a pending connection (has numeric ID)
          if (typeof connection.id === 'number') {
            void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
              void PostConnectionApi.delete(connection)
            })
          }
          // Remove from local state
          removeConnection(connection.id)
        })

        connectionsCreatedInSession.value.clear()
      }
    }
    postToConnectFromId.value = null
  }

  const cancelConnectionFromPost = ({ postId }: { postId: Id }): void => {
    if (postToConnectFromId.value !== postId) return
    cancelConnection()
  }

  const cancelDisconnection = (): void => {
    if (useMultipleConnections) {
      // Restore all disconnections created during this session
      if (disconnectionsCreatedInSession.value.length > 0) {
        const connectionsToRestore = [...disconnectionsCreatedInSession.value]
        let restoredCount = 0
        let failedCount = 0
        let notificationShown = false

        // Restore connections to local state immediately for instant UI feedback
        connectionsToRestore.forEach((connection) => {
          addConnection(connection)
        })

        // Helper function to check if all restorations are complete and show notification if needed
        const checkCompletion = (): void => {
          if (!notificationShown && restoredCount + failedCount === connectionsToRestore.length && failedCount > 0) {
            notificationShown = true
            globalSnackbarStore.setSnackbar({
              message: `Failed to restore ${failedCount} connection${failedCount > 1 ? 's' : ''} on the server. They are visible locally.`,
              persist: false,
            })
          }
        }

        // Sync with server by recreating each connection
        connectionsToRestore.forEach((connection) => {
          void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
            void PostConnectionApi.create({
              from_wish_id: connection.from_wish_id,
              to_wish_id: connection.to_wish_id,
              label: connection.label,
            })
              .then((connectionData: JsonAPIResponse<PostConnection>) => {
                const serverConnection = (connectionData.data as JsonAPIResource<PostConnection>).attributes
                // Remove the locally restored connection and add the server version
                // This ensures we have the correct server-assigned ID
                removeConnection(connection.id)
                addConnection(serverConnection)
                // Remove from tracking only after successful server restoration
                const index = disconnectionsCreatedInSession.value.findIndex((c) => c.id === connection.id)
                if (index !== -1) {
                  disconnectionsCreatedInSession.value.splice(index, 1)
                }
                restoredCount++
                checkCompletion()
              })
              .catch((e) => {
                // Connection is already visible locally from immediate restoration above
                // Keep it in tracking so user can see what happened
                captureNonNetworkFetchError(e, { source: 'cancelDisconnection', action: 'restoreConnection' })
                failedCount++
                checkCompletion()
              })
          })
        })
      }
      disconnectionSessionsFinishedByUser.value[disconnectionSessionId.value] = true
    }
    postToDisconnectFromId.value = null
  }

  const removeConnectionsForPost = ({ postId }: { postId: Id }): void => {
    const relatedConnections = connectionIds.value.filter((id) => {
      return connectionEntities.value[id].from_wish_id === postId || connectionEntities.value[id].to_wish_id === postId
    })
    removeConnections(relatedConnections)
  }

  const completeConnection = ({ postToConnectToId, label }: { postToConnectToId: Id; label: string }): void => {
    const newConnection = {
      from_wish_id: postToConnectFromId.value ?? undefined,
      to_wish_id: postToConnectToId,
      label,
    }

    if (useMultipleConnections) {
      // Track the pending connection ID
      const tempId = addPendingConnection(newConnection) as string
      connectionsCreatedInSession.value.add(tempId) // Capture session ID at connection creation time to prevent race conditions
      const creationSessionId = sessionId.value
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.create(newConnection)
          .then((connectionData: JsonAPIResponse<PostConnection>) => {
            const sessionWasFinishedByUser = sessionsFinishedByUser.value[creationSessionId] === true
            const createdConnection = (connectionData.data as JsonAPIResource<PostConnection>).attributes
            // Only process if still in same session
            if (creationSessionId !== sessionId.value) {
              // Session changed - only delete if session wasn't finished by user
              if (!sessionWasFinishedByUser) {
                // Session changed and wasn't finished by user, delete the orphaned connection from server
                void PostConnectionApi.delete(createdConnection)
                // Remove the pending connection from local state
                removeConnection(tempId)
                return
              }
              // Session changed but was finished by user - keep the connection
              // Remove tempId from tracking
              connectionsCreatedInSession.value.delete(tempId)
              addConnection(createdConnection)
              return
            }
            // Check if the session was canceled. A session is canceled if:
            // 1. Session wasn't finished by user
            // 2. AND (tempId is no longer tracked OR postToConnectFromId is null)
            const sessionWasCanceled =
              !sessionWasFinishedByUser &&
              (!connectionsCreatedInSession.value.has(tempId) || postToConnectFromId.value == null)

            if (sessionWasCanceled) {
              // Session was canceled, delete the created connection from server
              void PostConnectionApi.delete(createdConnection)
              // Remove the pending connection from local state if it still exists
              removeConnection(tempId)
              return
            }

            if (sessionWasFinishedByUser) {
              // Session finished by user, keep the connection
              // Remove tempId from tracking
              connectionsCreatedInSession.value.delete(tempId)
              addConnection(createdConnection)
              // If this was the last pending connection and session was finished, clear postToConnectFromId
              const remainingPendingCount = [...connectionsCreatedInSession.value].filter(
                (id) => typeof id === 'string',
              ).length
              if (remainingPendingCount === 0 && postToConnectFromId.value != null) {
                postToConnectFromId.value = null
              }
            } else {
              // Replace the temp ID with the actual connection ID in tracking
              connectionsCreatedInSession.value.delete(tempId)
              connectionsCreatedInSession.value.add(createdConnection.id)
              addConnection(createdConnection)
            }
          })
          .catch(() => {
            const sessionWasFinishedByUser = sessionsFinishedByUser.value[creationSessionId] === true
            // If API call fails, remove tempId from tracking
            connectionsCreatedInSession.value.delete(tempId)
            // Remove the failed connection from local state
            removeConnection(tempId)
            pendingConnectionIds.value.delete(tempId)
            // If session was finished and this was the last pending connection, clear postToConnectFromId
            if (sessionWasFinishedByUser) {
              // Check atomically after removal to avoid race conditions
              const remainingPendingCount = [...connectionsCreatedInSession.value].filter(
                (id) => typeof id === 'string',
              ).length
              if (remainingPendingCount === 0 && postToConnectFromId.value != null) {
                postToConnectFromId.value = null
              }
            }
          })
      })
    } else {
      postToConnectFromId.value = null
      addPendingConnection(newConnection)
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.create(newConnection).then((connectionData: JsonAPIResponse<PostConnection>) => {
          addConnection((connectionData.data as JsonAPIResource<PostConnection>).attributes)
        })
      })
    }
  }

  const completeDisconnection = ({ connectionId }: { connectionId: Id }): void => {
    const existingConnection = connectionEntities.value[connectionId]
    if (existingConnection == null) return

    if (useMultipleConnections) {
      // Store a copy of the connection before deleting
      const connectionCopy = { ...existingConnection }
      // Track the disconnection in the session
      disconnectionsCreatedInSession.value.push(connectionCopy)
      // Capture session state at disconnection time to prevent race conditions
      const currentDisconnectionSessionId = disconnectionSessionId.value
      // Remove from local state immediately
      removeConnection(existingConnection.id)
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.delete(existingConnection)
          .then(() => {
            // Check if the session was canceled or changed
            const sessionWasFinishedByUser =
              disconnectionSessionsFinishedByUser.value[currentDisconnectionSessionId] === true
            const sessionWasCanceled =
              !sessionWasFinishedByUser &&
              (postToDisconnectFromId.value == null || currentDisconnectionSessionId !== disconnectionSessionId.value)

            if (sessionWasCanceled) {
              // Session was canceled, restore the connection
              void PostConnectionApi.create({
                from_wish_id: connectionCopy.from_wish_id,
                to_wish_id: connectionCopy.to_wish_id,
                label: connectionCopy.label,
              })
                .then((connectionData: JsonAPIResponse<PostConnection>) => {
                  addConnection((connectionData.data as JsonAPIResource<PostConnection>).attributes)
                  // Remove from tracking only after successful restoration
                  const index = disconnectionsCreatedInSession.value.findIndex((c) => c.id === connectionCopy.id)
                  if (index !== -1) {
                    disconnectionsCreatedInSession.value.splice(index, 1)
                  }
                })
                .catch((e) => {
                  // If restoration fails, restore connection to local state to prevent data loss
                  addConnection(connectionCopy)
                  captureNonNetworkFetchError(e, { source: 'completeDisconnection', action: 'restoreConnection' })
                  globalSnackbarStore.setSnackbar({
                    message: 'Failed to restore connection. Please try again.',
                    persist: false,
                  })
                })
              return
            }

            if (sessionWasFinishedByUser) {
              // Session finished by user, keep the disconnection
              // Remove from tracking array
              const index = disconnectionsCreatedInSession.value.findIndex((c) => c.id === connectionCopy.id)
              if (index !== -1) {
                disconnectionsCreatedInSession.value.splice(index, 1)
              }
            }
          })
          .catch(() => {
            // If API call fails, restore the connection in local state
            addConnection(existingConnection)
            // Remove from tracking
            const index = disconnectionsCreatedInSession.value.findIndex((c) => c.id === connectionCopy.id)
            if (index !== -1) {
              disconnectionsCreatedInSession.value.splice(index, 1)
            }
          })
      })
    } else {
      postToDisconnectFromId.value = null
      removeConnection(existingConnection.id)
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.delete(existingConnection)
      })
    }
  }

  const updateConnection = ({ connectionId, label }: { connectionId: Id; label: string }): void => {
    const existingConnection = connectionEntities.value[connectionId]
    if (existingConnection == null) return

    // Store the optimistic label to identify our own updates when they come back via realtime
    if (!recentlyUpdatedConnectionLabels.value.has(connectionId)) {
      recentlyUpdatedConnectionLabels.value.set(connectionId, new Set())
    }
    recentlyUpdatedConnectionLabels.value.get(connectionId)?.add(label)

    connectionEntities.value[connectionId] = { ...existingConnection, label }
    void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
      void PostConnectionApi.update({ id: connectionId, label })
        .then((connectionData: JsonAPIResponse<PostConnection>) => {
          // Only update if no newer optimistic update has occurred
          const currentConnection = connectionEntities.value[connectionId]
          if (currentConnection != null && currentConnection.label === label) {
            connectionEntities.value[connectionId] = (connectionData.data as JsonAPIResource<PostConnection>).attributes
            setTimeout(() => {
              removePendingLabel(connectionId, label)
            }, 2000)
          } else {
            removePendingLabel(connectionId, label)
          }
        })
        .catch((e) => {
          removePendingLabel(connectionId, label)
          // If connection was deleted (404/410), remove it from state instead of restoring
          if (e.status === 404 || e.status === 410) {
            removeConnection(connectionId)
          } else {
            const currentConnection = connectionEntities.value[connectionId]
            if (currentConnection != null && currentConnection.label === label) {
              connectionEntities.value[connectionId] = existingConnection
            }
            if (e.status === 401) {
              globalSnackbarStore.setSnackbar(ACCESS_WITHDRAWN)
            } else {
              captureNonNetworkFetchError(e, { source: 'updateConnection' })
            }
          }
        })
    })
  }

  const newConnectionRemote = (connection: PostConnection): void => {
    addConnection(connection, true)
  }

  const deleteConnectionRemote = (connectionOrId: PostConnection | PostConnectionId): void => {
    const id = typeof connectionOrId === 'object' ? connectionOrId.id : connectionOrId
    removeConnection(id)
  }

  const finishConnection = (): void => {
    if (useMultipleConnections) {
      // If postToConnectFromId is already null, session may not have started yet (race: Done before startConnection).
      // Set flag so startConnection will not start; also retry once on nextTick in case startConnection runs first.
      if (postToConnectFromId.value == null) {
        connectionFinishRequested.value = true
        void nextTick(() => {
          if (postToConnectFromId.value != null) {
            // Session started, finish it now and clear the flag
            finishConnection()
          }
          // If postToConnectFromId is still null, keep the flag set so startConnection() will see it and abort
        })
        return
      }
      connectionFinishRequested.value = false
      // Mark session as finished by user so pending API calls don't treat it as canceled
      sessionsFinishedByUser.value[sessionId.value] = true
      const failedConnectionIds = [...connectionsCreatedInSession.value].filter(
        (id): id is string => typeof id === 'string',
      )
      if (failedConnectionIds.length > 0) {
        failedConnectionIds.forEach((tempId) => {
          removeConnection(tempId)
          pendingConnectionIds.value.delete(tempId)
          connectionsCreatedInSession.value.delete(tempId)
        })
      }
      postToConnectFromId.value = null
    }
  }

  const startConnection = ({ postId }: { postId: Id }): void => {
    if (useMultipleConnections && connectionFinishRequested.value) {
      connectionFinishRequested.value = false
      postToDisconnectFromId.value = null
      return
    }
    postToDisconnectFromId.value = null
    postToConnectFromId.value = postId
    if (useMultipleConnections) {
      // Initialize tracking for connections created in this session
      connectionsCreatedInSession.value.clear()
      sessionId.value++
      // Clean up old session entries
      if (sessionId.value > MAX_TRACKED_SESSIONS) {
        const oldSessionId = sessionId.value - MAX_TRACKED_SESSIONS
        delete sessionsFinishedByUser.value[oldSessionId]
      }
      if (snackBarUid.value == null) {
        snackBarUid.value = globalSnackbarStore.setSnackbar({
          message: 'Choose which posts to connect to',
          persist: true,
          actionText: 'Done',
          actionTextActions: [finishConnection],
        })
      }
    } else {
      if (snackBarUid.value == null) {
        snackBarUid.value = globalSnackbarStore.setSnackbar({
          message: 'Pick a post to connect to',
          persist: true,
          actionText: 'Cancel',
          actionTextActions: [cancelConnection],
        })
      }
    }
  }

  watch([xConnectionCancellation], () => {
    if (!xConnectionCancellation.value && snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
  })

  // When the connection or disconnection snackbar is dismissed, end the session as a fallback
  // TODO: ENG-32205 Update global snackbar to allow an optional onDismiss callback
  watch(
    () =>
      [
        globalSnackbarStore.snackbarArray,
        snackBarUid.value,
        postToConnectFromId.value,
        postToDisconnectFromId.value,
      ] as const,
    () => {
      if (snackBarUid.value == null || globalSnackbarStore.snackbarArray.some((s) => s.uid === snackBarUid.value)) {
        return
      }

      if (postToConnectFromId.value == null && postToDisconnectFromId.value == null) {
        snackBarUid.value = null
        return
      }

      void (postToConnectFromId.value != null
        ? useMultipleConnections
          ? finishConnection()
          : cancelConnection()
        : useMultipleConnections
          ? finishDisconnection()
          : cancelDisconnection())

      snackBarUid.value = null
    },
    { deep: true },
  )

  const finishDisconnection = (): void => {
    if (useMultipleConnections) {
      // Mark session as finished by user
      disconnectionSessionsFinishedByUser.value[disconnectionSessionId.value] = true
      postToDisconnectFromId.value = null
    }
  }

  const startDisconnection = ({ postId }: { postId: Id }): void => {
    // When starting a new disconnection session (same or different post), mark any existing
    // disconnection session as finished. This keeps the disconnections (user intentionally
    // removed them) and starts a new session. This matches the behavior of startConnection
    // which doesn't cancel previous sessions when switching posts.
    if (useMultipleConnections && postToConnectFromId.value != null) {
      // Mark the current session as finished before incrementing
      sessionsFinishedByUser.value[sessionId.value] = true
      sessionId.value++
      // Clean up old session entries
      if (sessionId.value > MAX_TRACKED_SESSIONS) {
        const oldSessionId = sessionId.value - MAX_TRACKED_SESSIONS
        delete sessionsFinishedByUser.value[oldSessionId]
      }
    }
    // Clear the connection snackbar before setting postToConnectFromId to null
    // Prevent the xConnectionCancellation watcher from removing the disconnection snackbar
    if (snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
    postToConnectFromId.value = null

    if (useMultipleConnections) {
      // If there's an existing disconnection session (same post or different), mark it as finished
      // This keeps the disconnections and starts a new session
      if (postToDisconnectFromId.value != null) {
        disconnectionSessionsFinishedByUser.value[disconnectionSessionId.value] = true
      }
      // Initialize tracking for disconnections created in this session
      disconnectionsCreatedInSession.value = []
      disconnectionSessionId.value++
      // Clean up old session entries
      if (disconnectionSessionId.value > MAX_TRACKED_SESSIONS) {
        const oldSessionId = disconnectionSessionId.value - MAX_TRACKED_SESSIONS
        delete disconnectionSessionsFinishedByUser.value[oldSessionId]
      }
    }

    postToDisconnectFromId.value = postId

    if (useMultipleConnections) {
      if (snackBarUid.value == null) {
        snackBarUid.value = globalSnackbarStore.setSnackbar({
          message: 'Choose which connections to remove',
          persist: true,
          actionText: 'Done',
          actionTextActions: [finishDisconnection],
        })
      }
    } else {
      snackBarUid.value = globalSnackbarStore.setSnackbar({
        message: 'Pick a connector to remove',
        persist: true,
        actionText: 'Cancel',
        actionTextActions: [cancelDisconnection],
      })
    }
  }

  watch([xDisconnectionCancellation], () => {
    if (!xDisconnectionCancellation.value && snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
  })

  return {
    // Getters
    connections,
    currentConnections,
    postToConnectFromId,
    postToDisconnectFromId,
    xConnectionCancellation,
    xDisconnectionCancellation,

    // Actions
    fetchConnections,
    cancelConnection,
    cancelConnectionFromPost,
    cancelDisconnection,
    removeConnectionsForPost,
    completeConnection,
    completeDisconnection,
    updateConnection,
    newConnectionRemote,
    deleteConnectionRemote,
    startConnection,
    finishConnection,
    startDisconnection,
    finishDisconnection,
    getConnectionsFromPost,
    getConnectionsToPost,
  }
})
export { useSurfacePostConnectionStore }
